import { z } from 'zod'


import { pageSchema } from './page'
import { trashPageMetaSchema } from './trash'

export const recordGroupSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  formId: z.string().uuid().nullable(),
  parentRecordGroupId: z.string().uuid().nullable(),
  // True when the user may only see this group's name to navigate to content further down the
  // tree (derived "trace" access); the group itself cannot be opened.
  traceOnly: z.boolean().optional().default(false),
  // Record tag applied automatically to every new record of the group.
  defaultRecordTagId: z.string().uuid().nullable().optional(),
  deleted: z.boolean().optional(),
  deletedAt: z.string().datetime().nullable().optional(),
})

export const recordGroupRefSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  traceOnly: z.boolean(),
})

/** One group with its visible ancestors (root first) for breadcrumbs. */
export const recordGroupDetailSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  formId: z.string().uuid().nullable(),
  parentRecordGroupId: z.string().uuid().nullable(),
  traceOnly: z.boolean(),
  defaultRecordTagId: z.string().uuid().nullable(),
  // Shared view opened first; null means the built-in "All records" list.
  defaultViewId: z.string().uuid().nullable().optional(),
  childCount: z.number().int().nonnegative(),
  ancestors: z.array(recordGroupRefSchema),
})

/** A row of the paged record group browser. recordCount is null for trace-only groups. */
export const recordGroupListItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  formId: z.string().uuid().nullable(),
  parentRecordGroupId: z.string().uuid().nullable(),
  traceOnly: z.boolean(),
  childCount: z.number().int().nonnegative(),
  recordCount: z.number().int().nonnegative().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  path: z.array(recordGroupRefSchema),
})

export const recordGroupListPageSchema = pageSchema(recordGroupListItemSchema)

export const recordGroupSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  topLevel: z.number().int().nonnegative(),
  nested: z.number().int().nonnegative(),
  openable: z.number().int().nonnegative(),
  withForm: z.number().int().nonnegative(),
})

export type RecordGroupSearchScope = 'CHILDREN' | 'SUBTREE'
export type RecordGroupSortField = 'NAME' | 'CREATED_AT' | 'UPDATED_AT'
export type SortDirection = 'ASC' | 'DESC'

export interface RecordGroupSearchParams {
  q?: string
  parentId?: string
  scope?: RecordGroupSearchScope
  hasForm?: boolean
  sort?: RecordGroupSortField
  dir?: SortDirection
  page: number
  size: number
}

export const createRecordGroupRequestSchema = z.object({
  name: z.string().min(1),
  parentRecordGroupId: z.string().uuid().nullable().optional(),
})

// Trash-specific schema matching DTOTrashRecordGroupResponse from the backend.
// Fields differ from recordGroupSchema: currentFormId instead of formId, plus workspaceId/ownerUserId.
export const trashedRecordGroupItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  currentFormId: z.string().uuid().nullable().optional(),
  parentRecordGroupId: z.string().uuid().nullable().optional(),
  workspaceId: z.string().uuid(),
  ownerUserId: z.string().uuid().nullable().optional(),
  deletedAt: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
})

export const trashedRecordGroupPageSchema = trashPageMetaSchema.extend({
  content: z.array(trashedRecordGroupItemSchema),
})

export type RecordGroup = z.infer<typeof recordGroupSchema>
export type RecordGroupRef = z.infer<typeof recordGroupRefSchema>
export type RecordGroupDetail = z.infer<typeof recordGroupDetailSchema>
export type RecordGroupListItem = z.infer<typeof recordGroupListItemSchema>
export type RecordGroupListPage = z.infer<typeof recordGroupListPageSchema>
export type RecordGroupSummary = z.infer<typeof recordGroupSummarySchema>
export type CreateRecordGroupRequest = z.infer<typeof createRecordGroupRequestSchema>
export type TrashedRecordGroupItem = z.infer<typeof trashedRecordGroupItemSchema>
export type TrashedRecordGroupPage = z.infer<typeof trashedRecordGroupPageSchema>